import { Timecode as TimecodeLib } from 'timecode';
import StringUtils from './stringUtils';

/**
 * Timecode object.
 * @class Timecode
 */
export default class Timecode {
  constructor(hour, minute, second, frames, framerate) {
    this.hour = isNaN(hour) ? 0 : parseInt(hour, 10);
    this.minute = isNaN(minute) ? 0 : parseInt(minute, 10);
    this.second = isNaN(second) ? 0 : parseInt(second, 10);
    this.frames = isNaN(frames) ? 0 : parseInt(frames, 10);
    this.framerate = isNaN(framerate) ? 30 : parseInt(framerate, 10);

    // Handle weird cases where the timecode object doesn't auto-increment 60 seconds to 1 minute always.
    if (this.second === 60) {
      this.minute += 1;
      this.second = 0;
    }

    if (this.minute === 60) {
      this.hour += 1;
      this.minute = 0;
    }

    this.timecode = TimecodeLib.init({
      framerate: this.framerate,
      timecode: `${Timecode.formatNumber(this.hour)}:${Timecode.formatNumber(
        this.minute
      )}:${Timecode.formatNumber(this.second)}:${Timecode.formatNumber(this.frames)}`
    });
  }

  /**
   * Parses a string to a timecode.
   * @func parseTimecode
   * @memberof Timecode
   * @static
   * @param  {string} str The string to convert.
   * @return {Timecode}       Timecode object
   */
  static parseTimecode(str, framerate) {
    const split = str.split(':');
    return new Timecode(split[0], split[1], split[2], split[3], framerate);
  }

  /**
   * Formats the number into a standard timecode form.
   * @func formatNumber
   * @memberof Timecode
   * @static
   * @param  {string} number Number to standardize.
   * @return {string}          The formatted string.
   */
  static formatNumber(number) {
    if (isNaN(number) || number > 99 || number < 0) {
      return '00';
    }
    return StringUtils.pad('00', number, true);
  }

  /**
   * Converts string timecode to seconds
   * @func convertToSeconds
   * @memberof Timecode
   * @param {Timecode} timecode  The timecode to convert to seconds.
   */
  static convertToSeconds(timecode, framerate) {
    if (isNaN(framerate) || framerate < 1) {
      return 0;
    }
    const newTimecode =
      typeof timecode === 'string' || timecode instanceof String
        ? Timecode.parseTimecode(timecode, framerate)
        : timecode;
    /* eslint-disable */
    return (
      newTimecode.hour * 60 * 60 +
      newTimecode.minute * 60 +
      newTimecode.second +
      newTimecode.frames / newTimecode.framerate
    );
    /* eslint-enable */
  }

  /**
   * Returns a timecode string.
   * @func getTimecodeString
   * @memberof Timecode
   * @return {string} A formatted timecode string.
   */
  getTimecodeString() {
    return this.timecode.toString();
  }

  /**
   * Adds two timecodes together.
   * @func add
   * @memberof Timecode
   * @param {Timecode} timecode The timecode to add to this timecode.
   */
  add(timecode) {
    if (!timecode || !(timecode instanceof Timecode)) {
      return this;
    }
    const newTimecode = this.timecode;

    this.timecode.add(timecode.timecode);

    return Timecode.parseTimecode(newTimecode.toString(), this.timecode.framerate);
  }

  /**
   * Subtracts one timecode from the other.
   * @func subtract
   * @memberof Timecode
   * @param {Timecode} timecode The timecode to subtract from this timecode.
   */
  subtract(timecode) {
    if (!timecode || !(timecode instanceof Timecode)) {
      return this;
    }
    const newTimecode = this.timecode;

    try {
      this.timecode.subtract(timecode.timecode);
    } catch (exception) {
      console.log(exception);
      return Timecode.parseTimecode('00:00:00:00', this.timecode.framerate);
    }

    return Timecode.parseTimecode(newTimecode.toString(), this.timecode.framerate);
  }
}

export const getTimeCode = (position, totalMs, frameRate = 30) => {
  // eslint-disable-next-line
  const ms = (position / 1000) * totalMs;
  const frames = Math.floor((ms % 1000) / (1000 / frameRate));
  const seconds = Math.floor(ms / 1000) % 60;
  const minutes = Math.floor(ms / 60000) % 60;
  const hours = Math.floor(ms / 3600000);

  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}:${pad(frames)}`;
};

const pad = value => value.toString().padStart(2, '0');



// WEBPACK FOOTER //
// ./app/util/timecode.js